/*
 * The strip's sparkles. PORTED from marketing-site
 * src/components/kit/SparklesScript.astro @ 920cba1 (sha256:e83417dfcb0f80ec)
 * with the TypeScript annotations removed, wrapped in a function so its names
 * stay out of the page's scope, and nothing else changed. Owner ruling,
 * 4 Oct 2026; ART_DIRECTION §5, "Sparkles on the strip". The rules it drives
 * are in assets/dg-sparkles.css. Change it there and re-copy.
 *
 * Loaded by snippets/dg-header.liquid, which carries the markup contract of
 * marketing-site src/components/kit/Sparkles.astro around "free shipping".
 * Since 4 Oct 2026 snippets/dg-footer.liquid carries a second host, around
 * the footer's `get 10% off` (owner ruling); this script needs no change for
 * it, because each host outside a strip is its own surface.
 */
(() => {
  /**
   * TWINKLE. A sparkle is added at a random interval and lives LIFE ms, so two
   * or three are alive at once. Each is three nested elements: the outer holds
   * the position and takes the burst, the middle scales up and back down, the
   * SVG turns. Scale and rotation on separate elements, after Josh Comeau's
   * "Animated Sparkles in React", so the two animations never fight over one
   * transform. Sparkles are made only while the strip is on screen — the header
   * hiding on scroll takes it off — and the tab is visible.
   *
   * BURST. A mouse entering the strip, or a touch or pen pressing it, sends the
   * sparkles alive at that moment outward from the phrase's centre while they
   * fade, on motion.pageTransition. Twinkling resumes REST ms later.
   *
   * REDUCED MOTION. Three static sparkles at fixed points; no twinkle, no burst.
   */
  const LIFE = 700;
  const REST = 3000;
  const MAX = 3;
  const NS = 'http://www.w3.org/2000/svg';
  /* A four-point star, concave sides, in a 68-unit box. */
  const STAR = 'M34 0C36 22 46 32 68 34C46 36 36 46 34 68C32 46 22 36 0 34C22 32 32 22 34 0Z';

  /* Fixed points for the static variant, as % of the phrase box, and sizes in px. */
  const STILL = [
    { x: -6, y: 30, size: 11 },
    { x: 62, y: -10, size: 12 },
    { x: 90, y: 110, size: 10 },
  ];

  const random = (min, max) => min + Math.random() * (max - min);

  /* A point AROUND the phrase rather than over it, as % of its box: before its
   * start, or in the band just above or below the line. A sparkle over the
   * words hides behind them (sparkles.css) and reads as nothing. Never past
   * its end, where the next word starts one space away. The bands are narrow
   * because the strip's padding is 8px and the strip clips what passes it.
   * A host marked data-dg-sparkles-around="vertical" is never sparkled before:
   * the free-shipping pill follows a price one space away. */
  const around = (vertical = false) => {
    const side = vertical ? random(0.25, 1) : Math.random();
    if (side < 0.25) return [random(-9, -3), random(15, 85)];
    if (side < 0.6) return [random(4, 96), random(-14, -4)];
    return [random(4, 96), random(104, 114)];
  };

  function make(x, y, size) {
    const outer = document.createElement('span');
    outer.className = 'dg-sparkle';
    outer.style.left = `${x}%`;
    outer.style.top = `${y}%`;
    outer.style.width = outer.style.height = `${size}px`;
    const scale = document.createElement('span');
    scale.className = 'dg-sparkle__scale';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 68 68');
    svg.setAttribute('class', 'dg-sparkle__spin');
    const path = document.createElementNS(NS, 'path');
    path.setAttribute('d', STAR);
    svg.append(path);
    scale.append(svg);
    outer.append(scale);
    return outer;
  }

  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  for (const host of document.querySelectorAll('[data-dg-sparkles]')) {
    const field = host.querySelector('.dg-sparkles__field');
    if (!field) continue;

    const max = Number(host.dataset.dgSparklesCount) || MAX;
    const vertical = host.dataset.dgSparklesAround === 'vertical';

    if (still) {
      for (const s of STILL.filter((p) => !vertical || p.x >= 0).slice(0, max)) field.append(make(s.x, s.y, s.size));
      continue;
    }

    /* The strip is the visible surface and the hit area; the phrase is where
     * the sparkles sit. A card's free-shipping pill takes the card as its
     * surface. Without either around it, the phrase is both. */
    const surface = host.closest('.dg-strip, .dg-rung, .dg-format') ?? host;
    let onScreen = false;
    let resting = false;
    let timer = 0;

    const running = () => onScreen && !resting && document.visibilityState === 'visible';

    const remove = (el) => el.remove();

    const add = () => {
      if (field.childElementCount >= max) return;
      const [x, y] = around(vertical);
      const el = make(x, y, Math.round(random(9, 14)));
      field.append(el);
      window.setTimeout(() => {
        if (!el.hasAttribute('data-dg-burst')) remove(el);
      }, LIFE);
    };

    const schedule = () => {
      window.clearTimeout(timer);
      if (!running()) return;
      timer = window.setTimeout(() => {
        add();
        schedule();
      }, random(180, 420));
    };

    const burst = () => {
      if (resting || !onScreen) return;
      resting = true;
      window.clearTimeout(timer);
      for (const el of field.querySelectorAll('.dg-sparkle:not([data-dg-burst])')) {
        /* Outward along the line from the phrase's centre to the sparkle. */
        const dx = parseFloat(el.style.left) - 50;
        const dy = (parseFloat(el.style.top) - 50) * 0.5;
        const len = Math.hypot(dx, dy) || 1;
        const reach = random(18, 30);
        el.setAttribute('data-dg-burst', '');
        el.style.setProperty('--sparkle-dx', `${(dx / len) * reach}px`);
        el.style.setProperty('--sparkle-dy', `${(dy / len) * reach}px`);
        /* The fallback for an end event that never comes (a hidden tab). */
        window.setTimeout(() => remove(el), 600);
      }
      window.setTimeout(() => {
        resting = false;
        schedule();
      }, REST);
    };

    surface.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') burst();
    });
    surface.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') burst();
    });

    new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      schedule();
    }).observe(surface);
    document.addEventListener('visibilitychange', schedule);
  }
})();
